/**
 * Mobile Storage Service for Android devices
 * Provides fallback mechanisms when File System Access API is not supported
 */

import { EEGDataPoint, PPGDataPoint, ACCDataPoint } from '../utils/SimpleCircularBuffer';
import { ProcessedEEGData } from '../types/eeg';
import { ProcessedPPGData, ProcessedACCData } from '../types/processed-data';
import { detectOS } from '../utils/pathUtils';
import { createLogger } from '../utils/logger';

const logger = createLogger('MobileStorage');

export interface MobileStorageConfig {
  autoDownload: boolean;
  downloadPath: string;
  maxSessionSize: number; // MB
  compressionEnabled: boolean;
}

export interface MobileSessionData {
  id: string;
  name: string;
  startTime: Date;
  eegData: EEGDataPoint[];
  ppgData: PPGDataPoint[];
  accData: ACCDataPoint[];
  processedEEGData: ProcessedEEGData[];
  processedPPGData: ProcessedPPGData[];
  processedACCData: ProcessedACCData[];
}

export class MobileStorageService {
  private static instance: MobileStorageService;
  private currentSession: MobileSessionData | null = null;
  private downloadQueue: Array<{ filename: string; content: string; mimeType: string }> = [];
  private config: MobileStorageConfig;
  private isAndroid: boolean;
  private downloadInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.isAndroid = detectOS() === 'android';
    this.config = {
      autoDownload: true,
      downloadPath: 'LINK_BAND_DATA',
      maxSessionSize: 50, // 50MB
      compressionEnabled: true
    };
  }

  static getInstance(): MobileStorageService {
    if (!MobileStorageService.instance) {
      MobileStorageService.instance = new MobileStorageService();
    }
    return MobileStorageService.instance;
  }

  /**
   * Check if File System Access API is supported
   */
  isFileSystemAccessSupported(): boolean {
    return 'showDirectoryPicker' in window;
  }

  /**
   * Check if device needs mobile storage fallback
   */
  needsMobileFallback(): boolean {
    return this.isAndroid && !this.isFileSystemAccessSupported();
  }

  /**
   * Start a new mobile session
   */
  async startSession(sessionId: string): Promise<boolean> {
    try {
      logger.info(`📱 [MobileStorage] Starting session: ${sessionId}`);
      
      // End existing session if any
      if (this.currentSession) {
        await this.endSession();
      }

      this.currentSession = {
        id: sessionId,
        name: `session_${sessionId}`,
        startTime: new Date(),
        eegData: [],
        ppgData: [],
        accData: [],
        processedEEGData: [],
        processedPPGData: [],
        processedACCData: []
      };

      // Start auto-download timer (every 30 seconds)
      if (this.config.autoDownload) {
        this.startAutoDownload();
      }

      logger.info(`✅ [MobileStorage] Session started: ${sessionId}`);
      return true;
    } catch (error) {
      console.error(`❌ [MobileStorage] Failed to start session: ${sessionId}`, error);
      return false;
    }
  }

  /**
   * Add EEG data to current session
   */
  addEEGData(data: EEGDataPoint[]): void {
    if (!this.currentSession) return;
    
    this.currentSession.eegData.push(...data);
    this.checkSessionSize();
  }

  /**
   * Add PPG data to current session
   */
  addPPGData(data: PPGDataPoint[]): void {
    if (!this.currentSession) return;
    
    this.currentSession.ppgData.push(...data);
    this.checkSessionSize();
  }

  /**
   * Add ACC data to current session
   */
  addACCData(data: ACCDataPoint[]): void {
    if (!this.currentSession) return;
    
    this.currentSession.accData.push(...data);
    this.checkSessionSize();
  }

  /**
   * Add processed EEG data to current session
   */
  addProcessedEEGData(data: ProcessedEEGData[]): void {
    if (!this.currentSession) return;
    
    this.currentSession.processedEEGData.push(...data);
    this.checkSessionSize();
  }

  /**
   * Add processed PPG data to current session
   */
  addProcessedPPGData(data: ProcessedPPGData[]): void {
    if (!this.currentSession) return;
    
    this.currentSession.processedPPGData.push(...data);
    this.checkSessionSize();
  }

  /**
   * Add processed ACC data to current session
   */
  addProcessedACCData(data: ProcessedACCData[]): void {
    if (!this.currentSession) return;
    
    this.currentSession.processedACCData.push(...data);
    this.checkSessionSize();
  }

  /**
   * Check session size and trigger download if needed
   */
  private checkSessionSize(): void {
    if (!this.currentSession) return;

    const sessionSizeMB = this.estimateSessionSize();
    if (sessionSizeMB > this.config.maxSessionSize) {
      logger.debug(`📱 [MobileStorage] Session size exceeded (${sessionSizeMB}MB), triggering download`);
      this.downloadCurrentSession();
    }
  }

  /**
   * Estimate current session size in MB
   */
  private estimateSessionSize(): number {
    if (!this.currentSession) return 0;

    const eegSize = this.currentSession.eegData.length * 50; // ~50 bytes per EEG point
    const ppgSize = this.currentSession.ppgData.length * 30; // ~30 bytes per PPG point
    const accSize = this.currentSession.accData.length * 25; // ~25 bytes per ACC point
    const processedSize = 
      this.currentSession.processedEEGData.length * 200 + // ~200 bytes per processed EEG
      this.currentSession.processedPPGData.length * 100 + // ~100 bytes per processed PPG
      this.currentSession.processedACCData.length * 80;   // ~80 bytes per processed ACC

    return (eegSize + ppgSize + accSize + processedSize) / (1024 * 1024);
  }

  /**
   * Start auto-download timer
   */
  private startAutoDownload(): void {
    if (this.downloadInterval) {
      clearInterval(this.downloadInterval);
    }

    this.downloadInterval = setInterval(() => {
      if (this.currentSession) {
        this.downloadCurrentSession();
      }
    }, 30000); // 30 seconds
  }

  /**
   * Download current session data
   */
  private async downloadCurrentSession(): Promise<void> {
    if (!this.currentSession) return;

    try {
      logger.info(`📱 [MobileStorage] Downloading session: ${this.currentSession.id}`);

      // Generate CSV files for each data type
      const files = await this.generateSessionFiles();
      
      // Download files
      for (const file of files) {
        this.downloadFile(file.filename, file.content, file.mimeType);
      }

      // Clear session data to free memory
      this.clearSessionData();

      logger.info(`✅ [MobileStorage] Session downloaded: ${this.currentSession.id}`);
    } catch (error) {
      console.error(`❌ [MobileStorage] Failed to download session`, error);
    }
  }

  /**
   * Generate files for current session
   */
  private async generateSessionFiles(): Promise<Array<{ filename: string; content: string; mimeType: string }>> {
    if (!this.currentSession) return [];

    const files: Array<{ filename: string; content: string; mimeType: string }> = [];
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const sessionName = `${this.currentSession.name}_${timestamp}`;

    // Generate metadata file
    const metadata = {
      sessionId: this.currentSession.id,
      sessionName: this.currentSession.name,
      startTime: this.currentSession.startTime.toISOString(),
      downloadTime: new Date().toISOString(),
      dataPoints: {
        eeg: this.currentSession.eegData.length,
        ppg: this.currentSession.ppgData.length,
        acc: this.currentSession.accData.length,
        processedEEG: this.currentSession.processedEEGData.length,
        processedPPG: this.currentSession.processedPPGData.length,
        processedACC: this.currentSession.processedACCData.length
      }
    };

    files.push({
      filename: `${sessionName}_metadata.json`,
      content: JSON.stringify(metadata, null, 2),
      mimeType: 'application/json'
    });

    // Generate EEG CSV
    if (this.currentSession.eegData.length > 0) {
      const eegCsv = this.convertEEGToCSV(this.currentSession.eegData);
      files.push({
        filename: `${sessionName}_eeg_raw.csv`,
        content: eegCsv,
        mimeType: 'text/csv'
      });
    }

    // Generate PPG CSV
    if (this.currentSession.ppgData.length > 0) {
      const ppgCsv = this.convertPPGToCSV(this.currentSession.ppgData);
      files.push({
        filename: `${sessionName}_ppg_raw.csv`,
        content: ppgCsv,
        mimeType: 'text/csv'
      });
    }

    // Generate ACC CSV
    if (this.currentSession.accData.length > 0) {
      const accCsv = this.convertACCToCSV(this.currentSession.accData);
      files.push({
        filename: `${sessionName}_acc_raw.csv`,
        content: accCsv,
        mimeType: 'text/csv'
      });
    }

    // Generate processed data files
    if (this.currentSession.processedEEGData.length > 0) {
      const processedEEGCsv = this.convertProcessedEEGToCSV(this.currentSession.processedEEGData);
      files.push({
        filename: `${sessionName}_eeg_processed.csv`,
        content: processedEEGCsv,
        mimeType: 'text/csv'
      });
    }

    return files;
  }

  /**
   * Convert EEG data to CSV
   */
  private convertEEGToCSV(data: EEGDataPoint[]): string {
    let csv = 'timestamp,fp1,fp2,signalQuality,leadOff_ch1,leadOff_ch2\n';
    
    for (const point of data) {
      csv += `${point.timestamp},${point.fp1},${point.fp2},${point.signalQuality},${point.leadOff.ch1 ? 1 : 0},${point.leadOff.ch2 ? 1 : 0}\n`;
    }
    
    return csv;
  }

  /**
   * Convert PPG data to CSV
   */
  private convertPPGToCSV(data: PPGDataPoint[]): string {
    let csv = 'timestamp,ir,red,leadOff_ch1,leadOff_ch2\n';
    
    for (const point of data) {
      csv += `${point.timestamp},${point.ir},${point.red},${point.leadOff.ch1 ? 1 : 0},${point.leadOff.ch2 ? 1 : 0}\n`;
    }
    
    return csv;
  }

  /**
   * Convert ACC data to CSV
   */
  private convertACCToCSV(data: ACCDataPoint[]): string {
    let csv = 'timestamp,x,y,z,magnitude\n';
    
    for (const point of data) {
      csv += `${point.timestamp},${point.x},${point.y},${point.z},${point.magnitude}\n`;
    }
    
    return csv;
  }

  /**
   * Convert processed EEG data to CSV
   */
  private convertProcessedEEGToCSV(data: ProcessedEEGData[]): string {
    let csv = 'timestamp,delta,theta,alpha,beta,gamma,attention,arousal,valence,relaxation,signalQuality,brainState\n';
    
    for (const point of data) {
      csv += `${point.timestamp},${point.bandPowers.delta},${point.bandPowers.theta},${point.bandPowers.alpha},${point.bandPowers.beta},${point.bandPowers.gamma},${point.brainState.metrics?.attention || 0},${point.brainState.metrics?.arousal || 0},${point.brainState.metrics?.valence || 0},${point.brainState.metrics?.relaxation || 0},${point.signalQuality.overall},${point.brainState.currentState}\n`;
    }
    
    return csv;
  }

  /**
   * Download file using browser download API
   */
  private downloadFile(filename: string, content: string, mimeType: string): void {
    try {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      logger.debug(`📱 [MobileStorage] File downloaded: ${filename}`);
    } catch (error) {
      console.error(`❌ [MobileStorage] Failed to download file: ${filename}`, error);
    }
  }

  /**
   * Clear session data to free memory
   */
  private clearSessionData(): void {
    if (!this.currentSession) return;

    this.currentSession.eegData = [];
    this.currentSession.ppgData = [];
    this.currentSession.accData = [];
    this.currentSession.processedEEGData = [];
    this.currentSession.processedPPGData = [];
    this.currentSession.processedACCData = [];
  }

  /**
   * End current session
   */
  async endSession(): Promise<boolean> {
    try {
      if (this.downloadInterval) {
        clearInterval(this.downloadInterval);
        this.downloadInterval = null;
      }

      if (this.currentSession) {
        logger.info(`📱 [MobileStorage] Ending session: ${this.currentSession.id}`);
        
        // Final download
        await this.downloadCurrentSession();
        
        // Clear session
        this.currentSession = null;
        
        logger.info(`✅ [MobileStorage] Session ended successfully`);
      }

      return true;
    } catch (error) {
      console.error(`❌ [MobileStorage] Failed to end session`, error);
      return false;
    }
  }

  /**
   * Get current session info
   */
  getCurrentSessionInfo(): MobileSessionData | null {
    return this.currentSession;
  }

  /**
   * Update mobile storage configuration
   */
  updateConfig(config: Partial<MobileStorageConfig>): void {
    this.config = { ...this.config, ...config };
    logger.debug(`📱 [MobileStorage] Config updated:`, this.config);
  }
} 